import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Drawer, Form, Input, Select, TreeSelect, Button, Table, Alert, Space, Row, Col, Typography, Modal, Image, Spin, Tooltip } from 'antd';
import { TABLE_PAGE_SIZE_OPTIONS, filterFormItemLayout, filterFormGridLayout } from '@utils/property';
import { convertDateTimeToLocalText, buildAuthDictMaps, formatOfficeAndPost, prepareBCOSSUrl, getAdminInfo, formatCurrency, numberProject } from '@utils/utils';
import { filterTreeNodeByTitle } from '@/utils/antdTreeSelect';
import { laborFeeCostBearers, laborFeePaymentMethods, expertLevels, CurrencyList, cardTypes } from '@utils/constants';
import { fetchLaborFeePaymentManageList, fetchLaborFeeList, fetchLaborFeeStatisticalAmount, fetchLaborFeeBatchStatisticalAmount } from '@services/Finance/FinanceLaborFee.service';
import { getConferenceListApi } from '@services/Agenda';
import { conferenceOnceApi } from '@services/Conference';
import { authdictionaryApi } from '@services/Conference/sign';
import { localStorageGet } from '@core/cookie';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';
import config from '@/config';
import ExpertInfoCard from '@/components/common/ExpertInfoCard';
import GuestTaskList from '@/components/common/GuestTaskList';
import FinanceApprovalDetailModal from './FinanceApprovalDetailModal';
import './LaborFeeApprovalDrawer.less';

const { Text } = Typography;

export default function LaborFeeApprovalDrawer({ open, onClose, projectId, projectCode = 'YHY', selectedBatchNo: propSelectedBatchNo }) {
    const navigate = useNavigate();
    const location = useLocation();

    const [filterForm] = Form.useForm();

    // 批次相关状态
    const [batchList, setBatchList] = useState([]);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [selectedBatchNo, setSelectedBatchNo] = useState(null);
    const [batchListLoading, setBatchListLoading] = useState(false);

    // 表格数据
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);

    // 统计信息
    const [batchStatistics, setBatchStatistics] = useState({});
    const [projectStatistics, setProjectStatistics] = useState({});

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [filterValues, setFilterValues] = useState({});

    // 参会凭证预览
    const [previewImage, setPreviewImage] = useState(null);

    // 会议场次树数据
    const [conferenceHallTreeData, setConferenceHallTreeData] = useState([]);

    // 认证字典数据
    const [authDict, setAuthDict] = useState([]);
    // 会议信息
    const [conference, setConference] = useState(null);

    // 审批详情弹窗
    const [approvalDetailVisible, setApprovalDetailVisible] = useState(false);

    // 根据认证字典构建映射
    const dictMaps = useMemo(() => buildAuthDictMaps(authDict), [authDict]);

    // 获取币种名称
    const getCurrencyName = currencyCode => {
        const currency = CurrencyList.find(c => c.currency === currencyCode);
        return currency ? currency.name : currencyCode;
    };

    // 获取付款批次列表
    const fetchBatchList = async () => {
        if (!projectId) return;
        try {
            setBatchListLoading(true);
            const res = await fetchLaborFeePaymentManageList({
                projectCode: projectCode,
                projectId: projectId,
                requestFrom: 2,
            });
            if (res.status_code === 200 && res.data && Array.isArray(res.data)) {
                // 直接使用接口返回的数据，无需复杂处理
                const batches = res.data.map(item => ({
                    ...item,
                    id: item.batchNo,
                }));

                setBatchList(batches);

                // 优先检查是否有由于 prop 传递的 propSelectedBatchNo，不然默认选中第一个批次
                if (batches.length > 0) {
                    let targetBatch = null;
                    if (propSelectedBatchNo) {
                        // 查找匹配的批次
                        targetBatch = batches.find(b => b.batchNo === propSelectedBatchNo);
                    }
                    // 如果没有找到匹配的批次或没有 propSelectedBatchNo，或 selectedBatchId 已经设置，默认选中第一个
                    if (!targetBatch && !selectedBatchId) {
                        targetBatch = batches[0];
                    } else if (targetBatch) {
                        // 会覆盖 selectedBatchId
                    } else {
                        // selectedBatchId 已设置，不需要更改
                        return;
                    }

                    if (targetBatch) {
                        setSelectedBatchId(targetBatch.id);
                        setSelectedBatchNo(targetBatch.batchNo);
                    }
                } else {
                    setSelectedBatchId(null);
                    setSelectedBatchNo(null);
                    setData([]);
                    setTotal(0);
                }
            } else {
                setBatchList([]);
                setSelectedBatchId(null);
                setSelectedBatchNo(null);
                setData([]);
                setTotal(0);
            }
        } catch (error) {
            console.error('获取付款批次列表失败:', error);
            setBatchList([]);
        } finally {
            setBatchListLoading(false);
        }
    };

    // 准备会议场次树数据
    const prepareConferenceHallTreeData = data => {
        const treeData = [];

        if (Object.keys(data) && Object.keys(data).length) {
            Object.keys(data).forEach(agendaDate => {
                const dateNode = {
                    title: agendaDate,
                    value: agendaDate,
                    selectable: false,
                    hallNum: 0,
                    children: [],
                };

                const confHalls = data[agendaDate];
                confHalls
                    && confHalls.length
                    && confHalls.forEach(confHall => {
                        const hallNode = {
                            title: `${confHall.name} [YHYC#${confHall.id}]`,
                            value: confHall.id,
                            children: [],
                        };

                        const confSubHalls = confHall.secondHallsList || [];
                        if (confSubHalls && confSubHalls.length) {
                            hallNode.selectable = false;

                            confSubHalls.forEach(subHall => {
                                dateNode.hallNum += 1;

                                hallNode.children.push({
                                    title: `${subHall.name} [YHYC#${subHall.id}]`,
                                    value: subHall.id,
                                });
                            });
                        } else {
                            dateNode.hallNum += 1;
                        }

                        dateNode.children.push(hallNode);
                    });

                treeData.push(dateNode);
            });
        }
        return treeData;
    };

    // 获取会议场次列表
    const getConferenceList = async () => {
        if (!projectId) return;
        try {
            const res = await getConferenceListApi({ conferenceId: projectId });
            if (res.status_code === 200 && res.data) {
                const agendaData = res.data;
                setConferenceHallTreeData(prepareConferenceHallTreeData(agendaData));
            }
        } catch (error) {
            console.error('获取会议场次列表失败:', error);
        }
    };

    // 请求认证字典数据
    const requestAuthDict = async () => {
        try {
            const res = await authdictionaryApi();
            if (!isEmpty(res?.options)) {
                const authDictData = res.options;
                authDictData
                    && Array.isArray(authDictData)
                    && authDictData.forEach(authType => {
                        authType.labels
                            && Array.isArray(authType.labels)
                            && authType.labels.forEach(label => {
                                label.options
                                    && Array.isArray(label.options)
                                    && label.options.forEach(l => {
                                        if (isEmpty(l.options)) {
                                            delete l.options;
                                        }
                                    });
                            });
                    });
                setAuthDict(authDictData);
            }
        } catch (error) {
            console.error('获取认证字典失败:', error);
        }
    };

    // 获取会议信息
    const getConferenceInfo = async () => {
        if (!projectId) return;
        try {
            const res = await conferenceOnceApi({ id: projectId });
            if (res.status_code === 200 && res.data) {
                setConference(res.data);
            }
        } catch (error) {
            console.error('获取会议信息失败:', error);
        }
    };

    // 选择批次
    const handleSelectBatch = batchId => {
        const batch = batchList.find(b => b.id === batchId);
        if (batch) {
            setSelectedBatchId(batchId);
            setSelectedBatchNo(batch.batchNo);
            setPage(1);
            setFilterValues({});
            filterForm.resetFields();
        }
    };

    // 获取项目劳务费统计总额（仅在弹窗打开时调用一次）
    const getProjectStatistics = async () => {
        if (!projectId) return;

        try {
            const params = {
                projectId,
                ...filterValues,
            };
            const res = await fetchLaborFeeStatisticalAmount(params);
            if (res && res.status_code === 200) {
                setProjectStatistics(res.data || {});
            }
        } catch (error) {
            console.error('获取项目劳务费统计总额失败:', error);
        }
    };

    // 获取批次劳务费统计总额（切换批次时调用）
    const getBatchStatistics = async () => {
        if (!projectId || !selectedBatchNo) return;

        try {
            const params = {
                projectId,
                batchNo: selectedBatchNo,
                ...filterValues,
            };
            const res = await fetchLaborFeeBatchStatisticalAmount(params);
            if (res && res.status_code === 200) {
                setBatchStatistics(res.data || {});
            }
        } catch (error) {
            console.error('获取批次劳务费统计总额失败:', error);
        }
    };

    // 获取劳务费明细列表
    const getLaborFeeList = async () => {
        if (!projectId || !selectedBatchNo) return;

        try {
            setLoading(true);
            const params = {
                page,
                pageSize,
                projectId,
                batchNo: selectedBatchNo,
                maskFlag: 1, // 默认脱敏，1-不脱敏
                ...filterValues,
            };

            const res = await fetchLaborFeeList(params);
            if (res && res.status_code === 200) {
                setData(res.data?.list || []);
                setTotal(res.data?.totalCount || 0);
            }
        } catch (error) {
            console.error('获取劳务费列表失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 表单提交
    const onFormFinish = values => {
        setFilterValues(values);
        setPage(1);
    };

    // 重置表单
    const onReset = () => {
        filterForm.resetFields();
        setFilterValues({});
        setPage(1);
    };

    // 页码改变
    const onChangePage = (newPage, newPageSize) => {
        setPage(newPage);
        setPageSize(newPageSize);
    };

    const getTotalAmount = (laborFeeSum, individualTaxSum) => {
        if (laborFeeSum !== null && individualTaxSum !== null) {
            return (parseFloat(laborFeeSum) + parseFloat(individualTaxSum)).toFixed(2);
        }
        return '0.00';
    };

    const toggleAgendaTasks = guestAgendaInfo => {
        setData(
            data.map(item => {
                if (item.expertId === guestAgendaInfo.expertId) {
                    return { ...item, expanded: !item.expanded };
                } else {
                    return item;
                }
            }),
        );
    };

    // 获取专家证件类型
    const getIdCardType = cardType => {
        const type = parseInt(cardType, 10);
        const target = cardTypes.find(item => item.code === type);
        return target?.name || null;
    };

    // 表格列定义
    const columns = [
        {
            title: '序号',
            width: 60,
            fixed: 'left',
            render: (_, __, index) => (page - 1) * pageSize + index + 1,
        },
        {
            title: '基本信息',
            width: 200,
            dataIndex: 'memUsername',
            fixed: 'left',
            render: (_, record) => <ExpertInfoCard expert={record} />,
        },
        {
            title: '单位任职',
            dataIndex: 'unitPost',
            width: 180,
            render: (_, row) => {
                if (row.workExpList?.length) {
                    return (
                        <>
                            {row.workExpList.map((item, index) => {
                                const officePostDisplay = formatOfficeAndPost(item, dictMaps);
                                const unitName = item.unitName || '';
                                const fullDisplay = [unitName, officePostDisplay].filter(Boolean).join('');
                                const displayIndex = index + 1;

                                return (
                                    <span key={`w_${item.id}`}>
                                        {row.workExpList.length > 1 && `${displayIndex}. `}
                                        {fullDisplay || ''}
                                    </span>
                                );
                            })}
                        </>
                    );
                } else {
                    return '—';
                }
            },
        },
        {
            title: '实际日程',
            dataIndex: 'agendasList',
            width: 400,
            render: (_, record) => {
                // 将 agendasList 映射为 agendasGuestsList 结构
                const agendasGuestsList = record.agendasList || [];
                return <GuestTaskList agendasGuestsList={agendasGuestsList} expanded={record.expanded} onToggleExpansion={toggleAgendaTasks} record={record} />;
            },
        },
        {
            title: '本次申请劳务费',
            dataIndex: 'currentBatchLaborFee',
            width: 250,
            render: (_, record) => {
                // 从付款申请记录中找到当前批次的数据
                const payRecords = record.laborFeePayRecordDtoList || [];
                const currentBatchRecord = payRecords.find(payRecord => payRecord.batchNo === selectedBatchNo);

                if (!currentBatchRecord) {
                    return <Text type="secondary">暂无</Text>;
                }

                const isForeignCurrency = currentBatchRecord.currency && currentBatchRecord.currency !== 'CNY';

                return (
                    <>
                        <div>
                            <Text>付款方式：</Text>
                            {currentBatchRecord.paymentMethod ? laborFeePaymentMethods.find(m => m.value === currentBatchRecord.paymentMethod)?.label : <Text type="secondary">&ndash;</Text>}
                        </div>
                        <div>
                            <Text>支付主体：</Text>
                            {currentBatchRecord.costBearer ? laborFeeCostBearers.find(b => b.value === currentBatchRecord.costBearer)?.label : <Text type="secondary">&ndash;</Text>}
                        </div>
                        <div>
                            <Text>支付币种：</Text>
                            {currentBatchRecord.currency ? getCurrencyName(currentBatchRecord.currency) : <Text type="secondary">&ndash;</Text>}
                        </div>
                        {isForeignCurrency ? (
                            <>
                                <div>
                                    <Text>外币实发额({getCurrencyName(currentBatchRecord.currency)})：</Text>
                                    {currentBatchRecord.foreignExchangeAmount ? formatCurrency(currentBatchRecord.foreignExchangeAmount) : <Text type="secondary">&ndash;</Text>}
                                </div>
                                <div>
                                    <Text>折合人民币实发劳务费(元)：</Text>
                                    {currentBatchRecord.laborFeeAmount ? formatCurrency(currentBatchRecord.laborFeeAmount) : <Text type="secondary">&ndash;</Text>}
                                </div>
                                <div>
                                    <Text>折合人民币个人所得税(元)：</Text>
                                    {currentBatchRecord.individualTaxAmount ? formatCurrency(currentBatchRecord.individualTaxAmount) : <Text type="secondary">&ndash;</Text>}
                                </div>
                                <div>
                                    <Text>折合人民币含税劳务费(元)：</Text>
                                    {currentBatchRecord.laborFeeAmount && currentBatchRecord.individualTaxAmount ? formatCurrency(parseFloat(currentBatchRecord.laborFeeAmount) + parseFloat(currentBatchRecord.individualTaxAmount)) : <Text type="secondary">&ndash;</Text>}
                                </div>
                                <div>
                                    <Text>专家参会照片盖章版：</Text>
                                    {currentBatchRecord.expertParticipationPic ? (
                                        <Button type="link" size="small" style={{ paddingLeft: 0 }} onClick={() => setPreviewImage(currentBatchRecord.expertParticipationPic)}>
                                            查看
                                        </Button>
                                    ) : (
                                        <Text type="warning">未上传</Text>
                                    )}
                                </div>
                                <div>
                                    <Text>专家劳务确认函盖章版：</Text>
                                    {currentBatchRecord.expertLaborFeeConfirmPic ? (
                                        <Button type="link" size="small" style={{ paddingLeft: 0 }} onClick={() => setPreviewImage(currentBatchRecord.expertLaborFeeConfirmPic)}>
                                            查看
                                        </Button>
                                    ) : (
                                        <Text type="warning">未上传</Text>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <Text>实发劳务费(元)：</Text>
                                    {currentBatchRecord.laborFeeAmount ? formatCurrency(currentBatchRecord.laborFeeAmount) : <Text type="secondary">&ndash;</Text>}
                                </div>
                                <div>
                                    <Text>个人所得税(元)：</Text>
                                    {currentBatchRecord.individualTaxAmount ? formatCurrency(currentBatchRecord.individualTaxAmount) : <Text type="secondary">&ndash;</Text>}
                                </div>
                                <div>
                                    <Text>含税劳务费(元)：</Text>
                                    {currentBatchRecord.laborFeeAmount && currentBatchRecord.individualTaxAmount ? formatCurrency(parseFloat(currentBatchRecord.laborFeeAmount) + parseFloat(currentBatchRecord.individualTaxAmount)) : <Text type="secondary">&ndash;</Text>}
                                </div>
                            </>
                        )}
                    </>
                );
            },
        },
        {
            title: '其他批次付款申请记录',
            dataIndex: 'laborFeePayRecordDtoList',
            width: 300,
            render: (_, record) => {
                const payRecords = record.laborFeePayRecordDtoList || [];
                // 过滤掉当前批次的记录
                const filteredPayRecords = payRecords.filter(payRecord => payRecord.batchNo !== selectedBatchNo);

                if (!filteredPayRecords || filteredPayRecords.length === 0) {
                    return <Text type="secondary">暂无</Text>;
                }

                // 按卡片格式展示所有付款记录（排除当前批次）
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {filteredPayRecords.map((payRecord, index) => {
                            const costBearerLabel = laborFeeCostBearers.find(b => b.value === payRecord.costBearer)?.label || <Text type="secondary">暂无</Text>;
                            const isForeignCurrency = payRecord.currency && payRecord.currency !== 'CNY';

                            return (
                                <div key={payRecord.batchNo} className="pay-record-card">
                                    <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                                        <div>
                                            申请批次：{convertDateTimeToLocalText(payRecord.createdAt)}
                                            {payRecord.createdName && `(${payRecord.createdName})`}
                                        </div>
                                        <div>支付主体：{costBearerLabel}</div>
                                        {isForeignCurrency ? (
                                            <>
                                                <div>
                                                    外币实发额({getCurrencyName(payRecord.currency)})：
                                                    {payRecord.foreignExchangeAmount ? `${formatCurrency(payRecord.foreignExchangeAmount)}` : <Text type="secondary">暂无</Text>}
                                                    <span>({!payRecord.approvalStatus || payRecord.approvalStatus === 1 ? <Text type="warning">待提交</Text> : payRecord.payedAt ? <Text type="success">已付款</Text> : <Text type="warning">待付款</Text>})</span>
                                                </div>
                                                <div>折合人民币实发劳务费(元)：{!!payRecord.laborFeeAmount ? `${formatCurrency(payRecord.laborFeeAmount)}` : <Text type="secondary">暂无</Text>}</div>
                                            </>
                                        ) : (
                                            <div>
                                                实发劳务费(元)：{!!payRecord.laborFeeAmount ? `${formatCurrency(payRecord.laborFeeAmount)}` : <Text type="secondary">暂无</Text>}
                                                <span>({!payRecord.approvalStatus || payRecord.approvalStatus === 1 ? <Text type="warning">待提交</Text> : payRecord.payedAt ? <Text type="success">已付款</Text> : <Text type="warning">待付款</Text>})</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            },
        },
        {
            title: '收款信息',
            dataIndex: 'bankName',
            width: 250,
            render: (_, record) => (
                <>
                    {(record.idcardType === 1 || !record.idcardType) && (
                        <>
                            <span>身份证号码：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY年M月D日')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户银行：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <div>{record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                        </>
                    )}
                    {record.idcardType > 1 && record.bankType !== 2 && (
                        <>
                            <span>证件类型：{getIdCardType(record.idcardType) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>证件号：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>国籍：{record.nationality || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY年M月D日')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生地：{record.birthPlace || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户银行：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <div>{record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                        </>
                    )}
                    {record.idcardType > 1 && record.bankType === 2 && (
                        <>
                            <span>证件类型：{getIdCardType(record.idcardType) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>证件号：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>国籍：{record.nationality || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY年M月D日')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生地：{record.birthPlace || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>收款人姓名(英文)：{record.payeeUsername || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>收款人银行预留电话：{record.payeePhone || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>收款人境外汇款住址(英文)：{record.payeeAddress || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户行名称(英文)：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>SWIFT/BIC Code：{record.swiftBicCode || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <div>{record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                        </>
                    )}
                </>
            ),
        },
        {
            title: '参会信息',
            dataIndex: 'signTime',
            width: 200,
            render: (_, record) => (
                <>
                    <div>签到时间：{record.signTime ? convertDateTimeToLocalText(record.signTime) : <Text type="secondary">暂无</Text>}</div>
                    <div>
                        签到方式：
                        {record.checkType === 1 && '线下核销签到'}
                        {record.checkType === 2 && '线上自助签到'}
                        {record.checkType === 3 && <Text type="danger">后台代签</Text>}
                        {!record.checkType && <Text type="secondary">暂无</Text>}
                    </div>
                    <div>
                        参会凭证：
                        {record.joinCert ? (
                            <Button type="link" size="small" style={{ paddingLeft: 0, paddingRight: 0 }} onClick={() => setPreviewImage(record.joinCert)}>
                                查看
                            </Button>
                        ) : (
                            <Text type="secondary">暂无</Text>
                        )}
                    </div>
                </>
            ),
        },
        {
            title: '付款信息',
            dataIndex: 'paymentInfo',
            width: 250,
            render: (_, record) => {
                // 从付款申请记录中找到当前批次的数据
                const payRecords = record.laborFeePayRecordDtoList || [];
                const currentBatchRecord = payRecords.find(payRecord => payRecord.batchNo === selectedBatchNo);

                if (!currentBatchRecord) {
                    return <Text type="secondary">暂无</Text>;
                }

                return (
                    <>
                        <div>
                            <Text>付款日期：</Text>
                            {currentBatchRecord.payedAt ? dayjs(currentBatchRecord.payedAt).format('YYYY年M月D日') : <Text type="secondary">暂无</Text>}
                        </div>
                        <div>
                            <Text>付款凭证：</Text>
                            {currentBatchRecord.paymentCert ? (
                                <Button type="link" size="small" style={{ paddingLeft: 0, paddingRight: 0 }} onClick={() => setPreviewImage(currentBatchRecord.paymentCert)}>
                                    查看
                                </Button>
                            ) : (
                                <Text type="secondary">暂无</Text>
                            )}
                        </div>
                        {currentBatchRecord.notes && (
                            <div>
                                <Alert
                                    title={
                                        <div>
                                            <span>{currentBatchRecord.notes}</span>
                                            <br />
                                            <span style={{ color: '#999' }}>{dayjs(currentBatchRecord.notesAt).format('YYYY年M月D日 HH:mm:ss')}</span>
                                        </div>
                                    }
                                    type="warning"
                                />
                            </div>
                        )}
                    </>
                );
            },
        },
    ];

    useEffect(() => {
        if (open && projectId) {
            requestAuthDict();
            getConferenceList();
            fetchBatchList();
            getConferenceInfo();
            // 弹窗打开时获取项目统计数据（只调用一次）
            getProjectStatistics();
        }
    }, [open, projectId]);

    useEffect(() => {
        if (open && projectId && selectedBatchNo) {
            getLaborFeeList();
            // 切换批次时只获取批次统计数据
            getBatchStatistics();
        }
    }, [open, projectId, selectedBatchNo, page, pageSize, filterValues]);

    // 关闭时重置状态
    const handleClose = () => {
        filterForm.resetFields();
        setFilterValues({});
        setPage(1);
        setPageSize(10);
        setData([]);
        setBatchList([]);
        setSelectedBatchId(null);
        setSelectedBatchNo(null);

        // 检查URL参数中是否有fromPage，如果有则返回到该页面
        const urlParams = new URLSearchParams(location.search || window.location.search);
        const fromPage = urlParams.get('fromPage');

        if (fromPage && fromPage !== window.location.pathname) {
            navigate(fromPage, { replace: true });
        } else {
            navigate({ pathname: location.pathname, search: '' }, { replace: true });
        }

        onClose();
    };

    // 获取当前选中批次的信息
    const currentBatch = useMemo(() => {
        return batchList.find(batch => batch.id === selectedBatchId);
    }, [batchList, selectedBatchId]);

    // 是否有权导出劳务费明细
    const canExportLaborFee = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList?.length) {
            return roleList.some(item => item.name === '劳务费管理员' || item.name === '财务劳务费管理员' || item.name === '财务劳务费付款人员');
        }
        return false;
    }, []);

    // 导出劳务费信息
    const exportLaborFee = () => {
        window.location.href = `${config.API_PREFIX}/user/laborAudit/pay-batch-infos-download?batchNo=${selectedBatchNo}`;
    };

    return (
        <Drawer title={<div style={{ textAlign: 'center', width: '100%' }}>付款申请批次</div>} placement="bottom" size="100%" open={open} onClose={handleClose} className="labor-fee-approval-drawer" destroyOnHidden closable={{ placement: 'end' }}>
            <div className="drawer-content-container">
                {/* 左侧：付款批次列表 */}
                <div className="batch-list-panel">
                    <div className="batch-list-content">
                        {batchListLoading ? (
                            <div className="batch-loading">
                                <Spin />
                            </div>
                        ) : batchList.length > 0 ? (
                            <div className="batch-items">
                                {batchList.map(batch => {
                                    const getBatchStatusClassName = approvalStatus => {
                                        if (batch.nodeId === 10 && approvalStatus === 3) {
                                            return 'batch-status--success';
                                        }
                                        const statusMap = {
                                            0: 'batch-status--default',
                                            1: 'batch-status--warning',
                                            2: 'batch-status--success',
                                            3: 'batch-status--error',
                                        };
                                        return statusMap[approvalStatus] || 'batch-status--default';
                                    };
                                    const statusClassName = getBatchStatusClassName(batch.approvalStatus);
                                    const formattedDate = batch.batchCreatedAt ? dayjs(batch.batchCreatedAt).format('YYYY年M月D日HH:mm:ss') : '—';

                                    // 构建第二行左侧显示内容：nodeName(approvalByName) 或 approvalByName
                                    const getNodeDisplay = () => {
                                        const nodeName = batch.nodeName || '';
                                        const approvalByName = batch.approvalByName || '';

                                        if (nodeName && approvalByName) {
                                            return `${nodeName}（${approvalByName}）`;
                                        } else if (nodeName) {
                                            return nodeName;
                                        }
                                        return '';
                                    };

                                    return (
                                        <div key={batch.id} className={`batch-item ${selectedBatchId === batch.id ? 'batch-item-selected' : ''}`} onClick={() => handleSelectBatch(batch.id)}>
                                            <div className="batch-date">付款批次：{formattedDate}</div>
                                            <div className={`batch-status ${statusClassName}`}>
                                                <span>{getNodeDisplay()}</span>
                                                <span>{batch.approvalStatusName}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="empty-state">暂无付款批次</div>
                        )}
                    </div>
                </div>

                {/* 右侧：筛选和表格 */}
                <div className="detail-panel">
                    {selectedBatchNo ? (
                        <>
                            {/* 筛选表单 */}
                            <div className="filter-form-box">
                                <Form {...filterFormItemLayout} form={filterForm} onFinish={onFormFinish} autoComplete="off">
                                    <Row>
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item name="memUsername" label="姓名">
                                                <Input placeholder="请输入专家姓名" allowClear />
                                            </Form.Item>
                                        </Col>
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item name="memMobile" label="手机号">
                                                <Input placeholder="请输入手机号" allowClear />
                                            </Form.Item>
                                        </Col>
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item label="专家级别" name="expertLevels">
                                                <Select mode="multiple" maxTagCount="responsive" options={expertLevels} fieldNames={{ label: 'label', value: 'level' }} placeholder="请选择专家级别" allowClear />
                                            </Form.Item>
                                        </Col>
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item name="conferenceHallId" label="任务所属会议">
                                                <TreeSelect showSearch={{ filterTreeNode: filterTreeNodeByTitle }} styles={{ popup: { root: { minWidth: 500, overflow: 'auto' } } }} allowClear treeDefaultExpandAll placement="bottomRight" treeData={conferenceHallTreeData} placeholder="请选择会议" />
                                            </Form.Item>
                                        </Col>
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item name="laborNeed" label="劳务需求">
                                                <Select placeholder="请选择" allowClear>
                                                    <Select.Option value={1}>需要</Select.Option>
                                                    <Select.Option value={2}>不需要</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item name="laborPayStatus" label="付款状态">
                                                <Select placeholder="请选择" allowClear>
                                                    <Select.Option value={0}>待付款</Select.Option>
                                                    <Select.Option value={1}>已付款</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item name="costBearer" label="支付主体">
                                                <Select placeholder="请选择" allowClear options={laborFeeCostBearers} fieldNames={{ label: 'label', value: 'value' }} />
                                            </Form.Item>
                                        </Col>
                                        <Col className="filter-form-buttons" {...filterFormGridLayout}>
                                            <Space>
                                                <Button type="primary" htmlType="submit">
                                                    检索
                                                </Button>
                                                <Button type="default" onClick={onReset}>
                                                    重置
                                                </Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Form>
                            </div>

                            {/* 审批详情按钮和导出按钮 */}
                            <div className="action-buttons">
                                <Space>
                                    {currentBatch && currentBatch.approvalStatus === 1 && currentBatch.approvalBy === getAdminInfo()?.adminPhone && (
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                setApprovalDetailVisible(true);
                                            }}
                                        >
                                            去审批
                                        </Button>
                                    )}
                                    <Button type="default" onClick={() => setApprovalDetailVisible(true)}>
                                        审批详情
                                    </Button>
                                    {canExportLaborFee && currentBatch && (
                                        <Tooltip title="导出劳务费明细">
                                            <Button
                                                icon={
                                                    <span className="anticon">
                                                        <i className="bi bi-cloud-download"></i>
                                                    </span>
                                                }
                                                onClick={exportLaborFee}
                                            />
                                        </Tooltip>
                                    )}
                                </Space>
                            </div>

                            {/* 统计信息 */}
                            <div className="statistics-alert">
                                <Alert
                                    type="info"
                                    style={{ marginBottom: 16, fontSize: 12 }}
                                    title={
                                        <>
                                            <span style={{ fontSize: 12, fontWeight: 600 }}>项目信息：</span>
                                            <span style={{ fontSize: 12 }}>
                                                {conference?.name || '-'}， 氚云项目编号：{conference?.h3yunNo || '-'}， 会议项目编号：{numberProject('YHY', conference?.id) || '-'}
                                            </span>
                                            <br />
                                            <span style={{ fontSize: 12, fontWeight: 600 }}>本批次付款信息：</span>
                                            <span style={{ fontSize: 12 }}>
                                                付款申请人数：{batchStatistics?.realTotal || 0}人， 已付款人数：{batchStatistics?.expertPaySum || 0}人， 实发劳务费合计金额（元）：{formatCurrency(batchStatistics?.LaborFeeSum || 0)}， 个人所得税合计金额（元）：{formatCurrency(batchStatistics?.IndividualTaxSum || 0)}，
                                                含税劳务费合计金额（元）：{formatCurrency(getTotalAmount(batchStatistics?.LaborFeeSum, batchStatistics?.IndividualTaxSum))}
                                            </span>
                                            <br />
                                            <span style={{ fontSize: 12, fontWeight: 600 }}>本项目累计申请付款信息（含本批次）：</span>
                                            <span style={{ fontSize: 12 }}>
                                                付款申请人数：{projectStatistics?.realTotal || 0}人， 已付款人数：{projectStatistics?.expertPaySum || 0}人， 实发劳务费合计金额（元）：{formatCurrency(projectStatistics?.LaborFeeSum || 0)}， 个人所得税合计金额（元）：
                                                {formatCurrency(projectStatistics?.IndividualTaxSum || 0)}， 含税劳务费合计金额（元）：{formatCurrency(getTotalAmount(projectStatistics?.LaborFeeSum, projectStatistics?.IndividualTaxSum))}
                                            </span>
                                            <br />
                                            <span style={{ fontSize: 12, fontWeight: 600 }}>劳务需求备注：</span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: '#FA8C16' }}>{conference?.laborFeeDemandRemarks || '未填写'}</span>
                                        </>
                                    }
                                />
                            </div>

                            {/* 表格 */}
                            <div className="table-box">
                                <Table
                                    rowKey="expertId"
                                    columns={columns}
                                    dataSource={data}
                                    loading={loading}
                                    scroll={{ x: 800 }}
                                    pagination={{
                                        total,
                                        showTotal: () => `共${total}条`,
                                        current: page,
                                        pageSize: pageSize,
                                        pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                                        showQuickJumper: true,
                                        showSizeChanger: true,
                                        onChange: onChangePage,
                                    }}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="empty-state-large">请选择付款批次</div>
                    )}
                </div>
            </div>

            {/* 图片预览 Modal */}
            <Modal title="图片预览" open={!!previewImage} onCancel={() => setPreviewImage(null)} footer={null} centered width="auto" styles={{ body: { padding: 0, textAlign: 'center', background: '#f0f0f0' } }} destroyOnHidden>
                {previewImage && <Image src={prepareBCOSSUrl(previewImage)} style={{ maxWidth: '90vw', maxHeight: '90vh' }} preview={false} alt="图片预览" />}
            </Modal>

            {/* 审批详情弹窗 */}
            {approvalDetailVisible && (
                <FinanceApprovalDetailModal
                    open={approvalDetailVisible}
                    onClose={() => setApprovalDetailVisible(false)}
                    paymentRecord={currentBatch}
                    projectId={projectId}
                    batchNo={selectedBatchNo}
                    statisticalAmount={batchStatistics}
                    onRefresh={() => {
                        fetchBatchList();
                        getLaborFeeList();
                        getBatchStatistics();
                    }}
                    currentUserPhone={getAdminInfo()?.adminPhone}
                    type="approval"
                />
            )}
        </Drawer>
    );
}
